import { IApiService } from './IApiService';
import { ApiService } from './ApiService';
import apiResponse, { ApiResponse } from './ApiResponse';

export enum PublicationStatus {
  Draft = 'DRAFT',
  Published = 'PUBLISHED',
  Retired = 'RETIRED',
}

/** The response types a questionnaire item can use. FreeText items have no choices. */
export enum QuestionnaireResponseType {
  MutuallyExclusive = 'MutuallyExclusive',
  // Pick one, shown as a dropdown; for long lists such as 0 to 30 days.
  Dropdown = 'Dropdown',
  FreeText = 'FreeText',
}

/**
 * A questionnaire's complete content, as the editor edits it and the publish endpoint takes it. Positions come
 * from array order. Codes are stable identities: reusing a code updates that questionnaire, instrument or item,
 * and a new code creates a new one.
 */
export interface QuestionnaireFormDefinition {
  code: string;
  name: string;
  // Only sent when publishing a version after the first, which requires it: why the version was made.
  changeReason?: string;
  instruments: InstrumentDefinition[];
}

export interface InstrumentDefinition {
  code: string;
  name: string;
  items: ItemDefinition[];
}

export interface ItemDefinition {
  code: string;
  text: string;
  leadIn?: string | null;
  responseType: string;
  // The subscale this item counts toward within its instrument, e.g. PHQ2.
  subscaleCode?: string | null;
  // Only sent when publishing a new version of an existing item: false when the change alters what the question means.
  comparableToPreviousVersion?: boolean;
  choices: ChoiceDefinition[];
}

export interface ChoiceDefinition {
  text: string;
  scoreValue: number | null;
}

export interface QuestionnaireFormVersionSummary {
  id: number;
  version: number;
  status: PublicationStatus;
  publishedAt: string | null;
  retiredAt: string | null;
  // Why it was published; null for a first version.
  changeReason: string | null;
}

export interface QuestionnaireFormSummary {
  id: number;
  code: string;
  name: string;
  // Null for a questionnaire with no versions yet.
  latestVersion: QuestionnaireFormVersionSummary | null;
  // Cohorts that ask this questionnaire now.
  cohortCount: number;
}

/** A questionnaire or instrument code the server scores, and what the questionnaire needs for it to work. */
export interface ScoredCode {
  code: string;
  name: string;
  // Plain sentences, e.g. "Exactly 4 questions".
  requirements: string[];
}

export interface ScoredCodes {
  questionnaires: ScoredCode[];
  instruments: ScoredCode[];
}

export interface QuestionnaireFormVersionDetail extends QuestionnaireFormVersionSummary {
  formId: number;
  definition: QuestionnaireFormDefinition;
}

export interface PublishQuestionnaireResult {
  formVersionId: number;
  version: number;
  // False when the definition matched the current version, so nothing was published.
  changed: boolean;
}

/** A shared timepoint label such as BASELINE or END. Schedules reuse them so research can compare END with END. */
export interface QuestionnaireTimepoint {
  id: number;
  code: string;
  name: string;
}

export interface QuestionnaireScheduleTimepoint {
  timepointId: number;
  timepointCode: string;
  timepointName: string;
  // The program day it opens, at midnight. Day 1 is the cohort's start date.
  opensOnDay: number;
  // Null when the timepoint never expires.
  windowDays: number | null;
}

export interface QuestionnaireSchedule {
  id: number;
  code: string;
  name: string;
  // True once any cohort has used the schedule. Its code and timing are then locked; only its name can change.
  inUse: boolean;
  // In the order they open.
  timepoints: QuestionnaireScheduleTimepoint[];
}

/** A schedule as the schedule editor saves it. */
export interface QuestionnaireScheduleDefinition {
  code: string;
  name: string;
  timepoints: QuestionnaireScheduleTimepointDefinition[];
}

export interface QuestionnaireScheduleTimepointDefinition {
  timepointId: number;
  opensOnDay: number;
  windowDays: number | null;
}

export interface QuestionnaireTimepointDefinition {
  code: string;
  name: string;
}

export enum SittingStatus {
  Upcoming = 'UPCOMING',
  Available = 'AVAILABLE',
  Completed = 'COMPLETED',
  Expired = 'EXPIRED',
}

export enum SittingTriggerType {
  Scheduled = 'SCHEDULED',
  ProviderRequested = 'PROVIDER_REQUESTED',
  SelfReported = 'SELF_REPORTED',
}

/** Every filter is optional. timepointId is the shared timepoint, so END matches END on any schedule. */
export interface QuestionnaireResultsFilter {
  cohortId?: number;
  formId?: number;
  timepointId?: number;
}

/** A cohort the results can be filtered to. */
export interface QuestionnaireResultsCohort {
  id: number;
  name: string;
}

export interface QuestionnaireScore {
  // Null for a score of the whole questionnaire rather than one instrument.
  instrumentCode: string | null;
  scaleCode: string;
  // e.g. "Healthy days"; null for a scale no scorer names.
  scaleName: string | null;
  // True when severityBand is the participant's answer (e.g. VERY_GOOD) and the value only its coding.
  answerOnly: boolean;
  value: number;
  severityBand: string | null;
}

/** One participant's sitting of a questionnaire at a timepoint. */
export interface QuestionnaireResult {
  sittingId: number;
  cohortId: number;
  cohortName: string;
  // Results are de-identified: a participant is shown only by their cohort user id, which is different on each
  // team they join. The server never sends their name or user id.
  cohortUserId: number;
  formCode: string;
  formName: string;
  // The version the participant was shown; null until it's first served.
  formVersion: number | null;
  // Null for ad hoc sittings.
  timepointCode: string | null;
  triggerType: SittingTriggerType;
  status: SittingStatus;
  availableAt: string;
  expiresAt: string | null;
  servedAt: string | null;
  completedAt: string | null;
  // The program day it was completed on; day 1 is the cohort's start date.
  completedDay: number | null;
  scores: QuestionnaireScore[];
}

export interface QuestionnaireAnswerChoice {
  id: number;
  text: string;
  scoreValue: number | null;
  selected: boolean;
}

/** One item as the participant saw it, with their answer. */
export interface QuestionnaireAnswer {
  itemCode: string;
  itemVersion: number;
  leadIn: string | null;
  text: string;
  responseType: string;
  choices: QuestionnaireAnswerChoice[];
  // Null when the item wasn't answered or isn't free text.
  freeText: string | null;
  // Null when the item wasn't answered.
  scoreValue: number | null;
}

export interface QuestionnaireResultDetail extends QuestionnaireResult {
  // Every item of the version the participant was shown, in order. Empty until the sitting is served.
  answers: QuestionnaireAnswer[];
}

export class QuestionnaireAdminStore {
  constructor(private readonly api: IApiService = new ApiService()) {}

  public async GetSummaries(): Promise<ApiResponse<QuestionnaireFormSummary[]>> {
    try {
      const response = await this.api.get('/QuestionnaireForm/summaries');
      return apiResponse.Success<QuestionnaireFormSummary[]>(response);
    } catch (e) {
      return apiResponse.HandleError(e);
    }
  }

  public async GetScoredCodes(): Promise<ApiResponse<ScoredCodes>> {
    try {
      const response = await this.api.get('/QuestionnaireForm/scored-codes');
      return apiResponse.Success<ScoredCodes>(response);
    } catch (e) {
      return apiResponse.HandleError(e);
    }
  }

  /** Newest first. */
  public async GetVersions(formId: number): Promise<ApiResponse<QuestionnaireFormVersionSummary[]>> {
    try {
      const response = await this.api.get(`/QuestionnaireForm/${formId}/versions`);
      return apiResponse.Success<QuestionnaireFormVersionSummary[]>(response);
    } catch (e) {
      return apiResponse.HandleError(e);
    }
  }

  public async GetVersion(formVersionId: number): Promise<ApiResponse<QuestionnaireFormVersionDetail>> {
    try {
      const response = await this.api.get(`/QuestionnaireForm/versions/${formVersionId}`);
      return apiResponse.Success<QuestionnaireFormVersionDetail>(response);
    } catch (e) {
      return apiResponse.HandleError(e);
    }
  }

  public async Publish(definition: QuestionnaireFormDefinition): Promise<ApiResponse<PublishQuestionnaireResult>> {
    try {
      const response = await this.api.post('/QuestionnaireForm/publish', definition);
      return apiResponse.Success<PublishQuestionnaireResult>(response);
    } catch (e) {
      return apiResponse.HandleError(e);
    }
  }

  public async GetSchedules(): Promise<ApiResponse<QuestionnaireSchedule[]>> {
    try {
      const response = await this.api.get('/QuestionnaireSchedule/details');
      return apiResponse.Success<QuestionnaireSchedule[]>(response);
    } catch (e) {
      return apiResponse.HandleError(e);
    }
  }

  public async CreateSchedule(definition: QuestionnaireScheduleDefinition): Promise<ApiResponse<{ id: number }>> {
    try {
      const response = await this.api.post('/QuestionnaireSchedule', definition);
      return apiResponse.Success<{ id: number }>(response);
    } catch (e) {
      return apiResponse.HandleError(e);
    }
  }

  /** Fails with 409 when a cohort uses the schedule and the code or timing changed. */
  public async UpdateSchedule(scheduleId: number, definition: QuestionnaireScheduleDefinition): Promise<ApiResponse> {
    try {
      const response = await this.api.put(`/QuestionnaireSchedule/${scheduleId}`, definition);
      return apiResponse.Success(response);
    } catch (e) {
      return apiResponse.HandleError(e);
    }
  }

  public async GetTimepoints(): Promise<ApiResponse<QuestionnaireTimepoint[]>> {
    try {
      const response = await this.api.get('/QuestionnaireTimepoint');
      return apiResponse.Success<QuestionnaireTimepoint[]>(response);
    } catch (e) {
      return apiResponse.HandleError(e);
    }
  }

  public async CreateTimepoint(definition: QuestionnaireTimepointDefinition): Promise<ApiResponse<{ id: number }>> {
    try {
      const response = await this.api.post('/QuestionnaireTimepoint', definition);
      return apiResponse.Success<{ id: number }>(response);
    } catch (e) {
      return apiResponse.HandleError(e);
    }
  }

  /** In the order they open. */
  public async GetResults(filter: QuestionnaireResultsFilter): Promise<ApiResponse<QuestionnaireResult[]>> {
    try {
      const query = new URLSearchParams(
        Object.entries(filter)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => [key, String(value)])
      ).toString();
      const response = await this.api.get(`/QuestionnaireResults${query ? `?${query}` : ''}`);
      return apiResponse.Success<QuestionnaireResult[]>(response);
    } catch (e) {
      return apiResponse.HandleError(e);
    }
  }

  /** Every cohort that has ever been assigned a questionnaire, newest first. */
  public async GetResultsCohorts(): Promise<ApiResponse<QuestionnaireResultsCohort[]>> {
    try {
      const response = await this.api.get('/QuestionnaireResults/cohorts');
      return apiResponse.Success<QuestionnaireResultsCohort[]>(response);
    } catch (e) {
      return apiResponse.HandleError(e);
    }
  }

  public async GetResult(sittingId: number): Promise<ApiResponse<QuestionnaireResultDetail>> {
    try {
      const response = await this.api.get(`/QuestionnaireResults/${sittingId}`);
      return apiResponse.Success<QuestionnaireResultDetail>(response);
    } catch (e) {
      return apiResponse.HandleError(e);
    }
  }
}
